/**
 * How a 1920 by 1080 canvas fits a viewport.
 *
 * The sizes come from the theme. A zero size keeps scale 1, so a first
 * paint before layout does not collapse the page.
 */

/** Below this, a tall page clips instead of shrinking further. */
export const TV_CONTENT_SCALE_FLOOR = 0.5

/**
 * How much taller a TV draws its charts, largest first.
 *
 * A chart does not reflow when the page lays out wider, so a grown chart can
 * push a page past the floor. The canvas then takes the next step down.
 */
export const TV_CHART_GROW_STEPS = [1.5, 1.25, 1] as const

/**
 * The uniform scale that fits the canvas in the viewport.
 *
 * @param viewportWidth - The viewport width, in CSS pixels.
 * @param viewportHeight - The viewport height, in CSS pixels.
 * @param canvasWidth - The canvas width, in CSS pixels.
 * @param canvasHeight - The canvas height, in CSS pixels.
 * @returns The scale. 1 when a size is missing.
 */
export function tvFitScale(
  viewportWidth: number,
  viewportHeight: number,
  canvasWidth: number,
  canvasHeight: number
): number {
  if (
    viewportWidth <= 0 ||
    viewportHeight <= 0 ||
    canvasWidth <= 0 ||
    canvasHeight <= 0
  ) {
    return 1
  }

  return Math.min(viewportWidth / canvasWidth, viewportHeight / canvasHeight)
}

/** The most layouts one fit tries. Each try is one page reflow. */
export const TV_FIT_STEPS = 8

/** The search stops when the fitting and overflowing scales are this close. */
const TV_FIT_TOLERANCE = 0.005

/**
 * The scale that fits tall content in the canvas and still fills its width.
 *
 * At scale s the page lays out at the canvas width divided by s, so after
 * the scale it fills the width. A wider layout is shorter, so the height
 * depends on the scale. The search keeps the largest scale known to fit and
 * the smallest known to overflow, and stops after a fixed number of steps.
 * It cannot oscillate, and the same heights always give the same scale.
 *
 * @param heightAt - The content height laid out for a scale, in CSS pixels.
 * @param availableHeight - The height the content may use, in CSS pixels.
 * @param floor - The smallest scale. Defaults to the TV floor.
 * @returns 1 when the content fits at full size. Otherwise the scale, at least the floor.
 */
export function tvFillScale(
  heightAt: (scale: number) => number,
  availableHeight: number,
  floor: number = TV_CONTENT_SCALE_FLOOR
): number {
  if (availableHeight <= 0) {
    return 1
  }
  const fullHeight = heightAt(1)
  if (fullHeight <= availableHeight) {
    return 1
  }

  // The floor fits by definition: below it the canvas clips.
  let fits = floor
  let overflows = 1
  let guess = availableHeight / fullHeight
  for (let step = 1; step < TV_FIT_STEPS; step += 1) {
    if (overflows - fits < TV_FIT_TOLERANCE) {
      break
    }
    if (!(guess > fits && guess < overflows)) {
      guess = (fits + overflows) / 2
    }
    const height = heightAt(guess)
    if (guess * height <= availableHeight) {
      fits = guess
    } else {
      overflows = guess
    }
    guess = height > 0 ? availableHeight / height : overflows
  }

  return fits
}
